import React, { useState, useEffect, useCallback } from "react"
import { createRoot } from "react-dom/client"
import clsx from "clsx"
import { Modal } from "../Modal"
import { sfxPlay } from "@/assets/audios/sfx"
import styles from "./index.module.scss"
import { i18n, I18nTextPath } from "@/i18n"
import { useDialogue } from "@/hooks/useDialogue"
import { hasCompletedStage, setGuideProgress, GuideStage } from "@/utils/utils"
import { showConfirm } from "../Confirm"
import { AnimatePresence, motion } from "motion/react"

export enum Skill {
    SKILL1 = "skill1",
    SKILL2 = "skill2",
    SKILL3 = "skill3",
    SKILL4 = "skill4",
    SKILL5 = "skill5",
    EMPTY = "empty",
}
export const SkillDialogContent: React.FC<{
    onClose: () => void
    resolve: (chooseSkills: Skill[]) => void
}> = ({ onClose: rawOnClose, resolve }) => {
    const [modalOpen, setModalOpen] = useState(true)
    const onClose = useCallback(() => {
        setModalOpen(false)
        setTimeout(() => {
            rawOnClose()
        }, 400)
    }, [rawOnClose])
    //已选技能
    const [chooseSkills, setChooseSkills] = useState<Skill[]>(
        new Array(3).fill(Skill.EMPTY)
    )
    //当前查看的技能
    const [currentSkill, setCurrentSkill] = useState<Skill>(Skill.SKILL1)

    //当前选中的已选技能
    const [currentChooseSkill, setCurrentChooseSkill] = useState<number>(-1)
    //是否需要引导
    const [isGuiding, setIsGuiding] = useState(false)
    const { startChooseGuideDialogue } = useDialogue()
    // // 控制引导提示的显示
    // const [showGuide, setShowGuide] = useState(false)
    useEffect(() => {
        if (!hasCompletedStage(GuideStage.SKILL_GUIDE)) {
            setIsGuiding(true)
            const timer = setTimeout(() => {
                startChooseGuideDialogue(() => {
                    setGuideProgress(GuideStage.SKILL_GUIDE)
                    setIsGuiding(false)
                })
            }, 500)
            return () => clearTimeout(timer)
        }
    }, [])
    const chooseSkill = (index: number) => {
        if (index === -1) {
            return
        }
        const newChooseSkills = [...chooseSkills]
        newChooseSkills[index] = currentSkill
        setChooseSkills(newChooseSkills)
    }

    const removeChooseSkill = (index: number) => {
        if (index === -1) {
            return
        }
        const newChooseSkills = [...chooseSkills]
        newChooseSkills[index] = Skill.EMPTY
        setChooseSkills(newChooseSkills)
        setCurrentChooseSkill(-1)
    }

    const handleConfirm = () => {
        if (isGuiding) {
            return
        }
        if (chooseSkills.indexOf(Skill.EMPTY) !== -1) {
            showConfirm({
                content: i18n.get("game.chooseSkillsTip"),
                onOk: () => {
                    resolve(chooseSkills)
                    onClose()
                },
                onCancel: () => {},
            })
            return
        }
        resolve(chooseSkills)
        onClose()
    }
    const handleClose = () => {
        if (isGuiding) {
            return
        }
        showConfirm({
            content: i18n.get("game.closeSkillTip"),
            onOk: () => {
                resolve(new Array(3).fill(Skill.EMPTY))
                onClose()
            },
            onCancel: () => {},
        })
    }

    return (
        <Modal
            open={modalOpen}
            showCloseBtn
            autoClose={false}
            onClose={handleClose}
            onConfirm={handleConfirm}
            maskClosable={false}
            modalClass={styles.skillDialogModal}
        >
            <div className={clsx(styles.skillContent)}>
                <div className={styles.title}>
                    {i18n.get("game.skillTitle")}
                </div>
                <div className={styles.tip}>
                    <div
                        className={clsx(styles.tipIcon, styles[currentSkill])}
                    ></div>
                    {i18n.get(`game.${currentSkill}` as I18nTextPath)}
                </div>
                <div className={styles.skillList}>
                    {Object.values(Skill)
                        .slice(0, 5)
                        .map((skill) => (
                            <div
                                className={styles.skillItem}
                                key={skill}
                                onClick={() => {
                                    sfxPlay("click")
                                    setCurrentSkill(skill)
                                }}
                            >
                                <div
                                    className={clsx(
                                        styles.choosedBg,
                                        currentSkill === skill && styles.active
                                    )}
                                ></div>
                                <img
                                    className={styles.skillIcon}
                                    src={require(
                                        `@/assets/imgs/skills/${skill}.png`
                                    )}
                                />
                            </div>
                        ))}
                </div>
                <div className={styles.chooseSkills}>
                    <div className={styles.chooseSkillsTitle}>
                        {i18n.get("game.chooseSkills")}
                    </div>
                    <div className={styles.chooseSkillsList}>
                        {chooseSkills.map((skill, index) => (
                            <div
                                key={index}
                                className={styles.chooseSkillsItem}
                                onClick={() => {
                                    sfxPlay("click")
                                    if (skill === Skill.EMPTY) {
                                        chooseSkill(index)
                                    } else if (currentChooseSkill === index) {
                                        removeChooseSkill(index)
                                    } else {
                                        setCurrentChooseSkill(index)
                                    }
                                }}
                            >
                                <div
                                    className={clsx(
                                        styles.choosedBg,
                                        skill === Skill.EMPTY && styles.hidden,
                                        currentChooseSkill === index &&
                                            styles.active
                                    )}
                                />
                                <div className={styles.bg} />
                                <div className={clsx(styles.chooseMask)}>
                                    <img
                                        className={clsx(
                                            styles.chooseIcon,
                                            styles.addMask,
                                            skill !== Skill.EMPTY &&
                                                styles.hidden
                                        )}
                                        src={require("@/assets/imgs/skills/choose_icon.png")}
                                    />
                                    <div
                                        className={clsx(
                                            styles.text,
                                            styles.addMask,
                                            skill !== Skill.EMPTY &&
                                                styles.hidden
                                        )}
                                    >
                                        {i18n.get("game.choose")}
                                    </div>
                                </div>

                                <AnimatePresence>
                                    {skill !== Skill.EMPTY && (
                                        <motion.div
                                            key={skill}
                                            onClick={() => {
                                                setCurrentChooseSkill(index)
                                            }}
                                            className={styles.skillIcon}
                                            initial={{ opacity: 0, scale: 0.8 }}
                                            animate={{ opacity: 1, scale: 1 }}
                                            exit={{ opacity: 0 }}
                                            transition={{
                                                duration: 0.2,
                                                ease: "easeOut",
                                            }}
                                        >
                                            <img
                                                src={require(
                                                    `@/assets/imgs/skills/${skill}.png`
                                                )}
                                                style={{
                                                    width: "100%",
                                                    height: "100%",
                                                }}
                                                alt=""
                                            />
                                            <div
                                                className={clsx(
                                                    styles.chooseMask,

                                                    currentChooseSkill !==
                                                        index && styles.hidden
                                                )}
                                            >
                                                <img
                                                    className={
                                                        styles.chooseIcon
                                                    }
                                                    src={require("@/assets/imgs/skills/download_icon.png")}
                                                />
                                                <div className={styles.text}>
                                                    {i18n.get("game.remove")}
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export const showSkillDialog = (): Promise<Skill[]> => {
    return new Promise(async (resolve) => {
        // 创建一个临时的 DOM 容器
        const container = document.createElement("div")
        document.body.appendChild(container)
        const root = createRoot(container)

        const handleClose = () => {
            // 卸载组件并移除容器
            root.unmount()
            document.body.removeChild(container)
        }
        await new Promise((resolve) => setTimeout(resolve, 400))

        // 渲染组件
        root.render(
            <SkillDialogContent onClose={handleClose} resolve={resolve} />
        )
    })
}
